import React, { createContext, useContext, useMemo, useRef, ReactNode } from 'react';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiry: number;
}

interface DataCache {
  get: <T>(key: string) => T | null;
  set: <T>(key: string, data: T, ttlSeconds?: number) => void;
  invalidate: (key: string) => void;
  invalidatePattern: (pattern: string) => void;
  clear: () => void;
}

const DataCacheContext = createContext<DataCache | null>(null);

export const useDataCache = (): DataCache => {
  const context = useContext(DataCacheContext);
  if (!context) {
    throw new Error('useDataCache must be used within a DataCacheProvider');
  }
  return context;
};

interface DataCacheProviderProps {
  children: ReactNode;
}

export const DataCacheProvider: React.FC<DataCacheProviderProps> = ({ children }) => {
  const cacheRef = useRef<Map<string, CacheEntry<any>>>(new Map());

  // Identità stabile: i consumatori tengono `cache` fra le dependency dei propri
  // effect (vedi useCachedUserProfile), quindi un oggetto nuovo a ogni render li
  // farebbe ripartire tutti. Oggi il provider sta in App, che non rirenderizza,
  // ma la dipendenza resterebbe una mina se il provider finisse più in basso.
  const cache: DataCache = useMemo(() => ({
    get: <T,>(key: string): T | null => {
      const entry = cacheRef.current.get(key);
      if (!entry) return null;
      
      if (Date.now() > entry.expiry) {
        cacheRef.current.delete(key);
        return null;
      }
      
      console.log(`[DataCache] HIT: ${key}`);
      return entry.data as T;
    },

    set: <T,>(key: string, data: T, ttlSeconds: number = 300): void => {
      const expiry = Date.now() + (ttlSeconds * 1000);
      cacheRef.current.set(key, { data, timestamp: Date.now(), expiry });
      console.log(`[DataCache] SET: ${key} (TTL: ${ttlSeconds}s)`);
    },

    invalidate: (key: string): void => {
      cacheRef.current.delete(key);
      console.log(`[DataCache] INVALIDATED: ${key}`);
    },

    invalidatePattern: (pattern: string): void => {
      const regex = new RegExp(pattern);
      const keysToDelete: string[] = [];
      
      cacheRef.current.forEach((_, key) => {
        if (regex.test(key)) {
          keysToDelete.push(key);
        }
      });
      
      keysToDelete.forEach(key => cacheRef.current.delete(key));
      console.log(`[DataCache] INVALIDATED PATTERN: ${pattern} (${keysToDelete.length} keys)`);
    },

    clear: (): void => {
      cacheRef.current.clear();
      console.log(`[DataCache] CLEARED ALL`);
    }
  }), []);

  return (
    <DataCacheContext.Provider value={cache}>
      {children}
    </DataCacheContext.Provider>
  );
};